import React from "react";
import { LogoWordmark } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Link } from "react-router-dom";
import { Lock, ShieldCheck, Fingerprint } from "lucide-react";
import { TRUST_BADGES } from "@/lib/suite";
import LabsLink from "@/components/LabsLink";
import FounderPromo from "@/components/FounderPromo";
import { useBranding, DEFAULT_BRANDING } from "@/context/BrandingContext";

const PILLARS = [
  { word: "Connect", desc: "Unify CRM, ERP and finance into one governed layer." },
  { word: "Clean", desc: "Automated hygiene keeps every record audit-ready." },
  { word: "Govern", desc: "Limits, policy and access — enforced in one control plane." },
];

function TrustBadge({ label }) {
  return (
    <span className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white/70">
      {label}
    </span>
  );
}

export function AuthShell({ children, wide = false, promo = null }) {
  const branding = useBranding();
  // The founder card and cross-product promo only show on Pravadh's own
  // install; white-label installs keep the pillars and trust badges.
  const showPromo = !!promo && branding.product_name === DEFAULT_BRANDING.product_name;
  return (
    <div className="flex min-h-screen bg-background">
      <div className="relative hidden w-[44%] shrink-0 overflow-hidden gx-navy lg:flex lg:flex-col">
        <div className="absolute inset-0 mesh-bg opacity-80" />
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="pointer-events-none absolute -right-24 top-16 h-72 w-72 rounded-full bg-[#1E5CEE]/20 blur-3xl" />
        <div className="pointer-events-none absolute -left-16 bottom-10 h-64 w-64 rounded-full bg-[#ff7a59]/15 blur-3xl" />

        <div className="relative z-10 flex h-full flex-col justify-between gap-8 overflow-y-auto p-12">
          <div className="[&_.text-foreground]:text-white [&_.text-primary]:text-[#7aa2ff]">
            <Link to="/" aria-label="Go to home" data-testid="auth-logo-home-desktop" className="inline-block transition-transform hover:scale-[1.02]">
              <LogoWordmark size={44} />
            </Link>
            <LabsLink
              arrow
              testid="auth-labs-desktop"
              className="mt-4 flex w-fit items-center gap-1 text-xs font-semibold text-white/80 hover:text-white hover:underline"
            >
              Pravadh Labs
            </LabsLink>
          </div>

          <div className="max-w-md">
            <p className="gx-label auth-fade-up">Data governance platform</p>
            <h1 className="auth-fade-up auth-delay-100 mt-3 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white lg:text-[2.75rem]">
              One control plane for
              <br />
              <span className="text-[#7aa2ff]">every connected system.</span>
            </h1>
            <p className="auth-fade-up auth-delay-200 mt-5 max-w-sm text-sm leading-relaxed text-slate-300">
              Extract data. Govern every limit. CRM, ERP and finance — with AES-256 encryption and a SHA-256 audit chain.
            </p>
            {showPromo ? (
              <div className="auth-fade-up auth-delay-300 mt-8">
                <FounderPromo site="GX" page={promo} />
              </div>
            ) : (
            <>
            <div className="auth-fade-up auth-delay-300 mt-10 space-y-5">
              {PILLARS.map((w) => (
                <div key={w.word} className="flex items-start gap-4">
                  <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#ff7a59]" />
                  <div>
                    <p className="font-display text-base font-bold text-white">{w.word}</p>
                    <p className="text-sm text-slate-400">{w.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="auth-fade-up auth-delay-400 mt-8 flex flex-wrap gap-2" data-testid="auth-trust-badges">
              {TRUST_BADGES.map((b) => (
                <TrustBadge key={b} label={b} />
              ))}
            </div>
            </>
            )}
          </div>

          <div className="flex items-center gap-5 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5"><Lock className="h-3 w-3" /> Private by default</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3 w-3" /> SOC-2 ready</span>
            <span className="inline-flex items-center gap-1.5"><Fingerprint className="h-3 w-3" /> JWT sessions</span>
          </div>
        </div>
      </div>

      <div className="relative flex flex-1 flex-col bg-background">
        <header className="flex items-center justify-between p-6 lg:px-12">
          <div className="lg:hidden">
            <Link to="/" aria-label="Go to home" data-testid="auth-logo-home-mobile" className="inline-block">
              <LogoWordmark size={36} />
            </Link>
          </div>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-3">
            <LabsLink
              arrow
              testid="auth-labs"
              className="inline-flex items-center gap-1 text-sm font-semibold text-foreground hover:underline"
            >
              Pravadh Labs
            </LabsLink>
            <ThemeToggle />
          </div>
        </header>

        <main className="flex flex-1 items-start justify-center px-6 pb-12 lg:items-center lg:px-12">
          <div className={wide ? "w-full max-w-3xl" : "w-full max-w-md"}>{children}</div>
        </main>
      </div>
    </div>
  );
}

export { Link };
